import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, MapPin, Users, Calendar, Clock, ShieldCheck, BadgeCheck, Star,
  ArrowRight, Building2, Factory, Cctv, Siren, FileText, GitCompare, Handshake, TrendingUp,
  Wallet, GraduationCap,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Stars, VerifiedBadge, inr } from "@/components/common";
import Seo, { SITE_URL } from "@/components/Seo";

const CATEGORY_ICONS = { "Security Guards": ShieldCheck, "Industrial Security": Factory, "Commercial Security": Building2, "Technology Security": Cctv, "Special Services": Siren };

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-heading text-3xl sm:text-4xl font-extrabold text-navy-900">{value}</div>
      <div className="brand-label text-navy-500 mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [form, setForm] = useState({ location: "", requirement: "", guards: "", start: "", duration: "" });

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/services").then((r) => { setCats(r.data); setServices([...new Set(r.data.flatMap((c) => c.services || []))]); }).catch(() => {});
    api.get("/cities").then((r) => setCities([...new Set((Array.isArray(r.data) ? r.data : []).map((c) => (typeof c === "string" ? c : c.city)).filter(Boolean))])).catch(() => {});
    api.get("/agencies").then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const search = () => {
    const p = new URLSearchParams();
    const loc = form.location.trim();
    if (loc) p.set(/^\d{6}$/.test(loc) ? "pincode" : "city", loc);
    if (form.requirement) p.set("category", form.requirement);
    if (form.guards) p.set("guards", String(form.guards).trim());
    if (form.start) p.set("start", form.start);
    if (form.duration) p.set("duration", form.duration.trim());
    navigate(`/agencies?${p.toString()}`);
  };

  return (
    <div>
      <Seo
        title="GuardDekho.com | Security Guard Marketplace in India"
        description="GuardDekho.com is a security guard marketplace connecting customers with verified security agencies and security guards across India. Post a requirement, compare PSARA-verified agencies and hire in minutes."
        canonical={`${SITE_URL}/`}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy-900">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/290275/pexels-photo-290275.jpeg)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 hero-grid opacity-[0.07]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-brand brand-label">
              <BadgeCheck size={14} /> PSARA Verified Security Agencies
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[1.05]">
              Find Verified Security<br />Services <span className="text-brand">Near You</span>
            </h1>
            <p className="mt-6 text-lg text-navy-300 max-w-2xl leading-relaxed">
              <strong className="text-white font-semibold">GuardDekho.com is a security guard marketplace connecting customers with verified security agencies and security guards in India.</strong> Compare quotations and manage your complete security requirement from one platform.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="bg-brand hover:bg-brand-600 text-white font-semibold h-12 px-7" onClick={() => navigate("/post-requirement")} data-testid="hero-get-quote-btn">
                Get Security Quote <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-7 border-white/25 bg-white/5 text-white hover:bg-white hover:text-navy-900" onClick={() => navigate("/agencies")} data-testid="hero-find-agency-btn">
                Find Security Agency
              </Button>
            </div>
          </motion.div>

          {/* SEARCH FORM */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-12 bg-white rounded-2xl shadow-2xl p-5 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <Field icon={MapPin} label="Location / Pincode">
              <div className="relative">
                <Input placeholder="City or 6-digit pincode" value={form.location}
                  onChange={(e) => { setForm({ ...form, location: e.target.value }); setCityOpen(true); }}
                  onFocus={() => setCityOpen(true)} onBlur={() => setTimeout(() => setCityOpen(false), 150)}
                  data-testid="search-location" className="border-navy-200" autoComplete="off" />
                {cityOpen && !/^\d+$/.test(form.location) && (() => {
                  const q = form.location.trim().toLowerCase();
                  const list = cities.filter((c) => !q || c.toLowerCase().includes(q)).slice(0, 8);
                  return list.length ? (
                    <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border border-navy-200 rounded-md shadow-lg max-h-56 overflow-auto" data-testid="city-suggestions">
                      {list.map((c) => (
                        <li key={c} onMouseDown={(e) => { e.preventDefault(); setForm({ ...form, location: c }); setCityOpen(false); }}
                          className="px-3 py-2 text-sm text-navy-900 hover:bg-navy-50 active:bg-navy-100 cursor-pointer">{c}</li>
                      ))}
                    </ul>
                  ) : null;
                })()}
              </div>
            </Field>
            <Field icon={ShieldCheck} label="Requirement">
              <select value={form.requirement} onChange={(e) => setForm({ ...form, requirement: e.target.value })} data-testid="search-requirement" className="flex h-10 w-full rounded-md border border-navy-200 bg-white px-3 py-2 text-sm text-navy-900">
                <option value="">Select requirement</option>
                {services.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
              </select>
            </Field>
            <Field icon={Users} label="Guards">
              <Input type="number" min="1" inputMode="numeric" placeholder="10" value={form.guards} onChange={(e) => setForm({ ...form, guards: e.target.value })} data-testid="search-guards" className="border-navy-200" />
            </Field>
            <Field icon={Calendar} label="Start Date">
              <Input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="search-start" className="border-navy-200" />
            </Field>
            <Field icon={Clock} label="Duration">
              <Input placeholder="12 Months" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="search-duration" className="border-navy-200" />
            </Field>
            <Button className="h-10 bg-navy-900 hover:bg-navy-800" onClick={search} data-testid="search-submit-btn">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
          </motion.div>
          <p className="mt-3 text-sm text-navy-400">e.g. Ahmedabad · 10 Security Guards · 12 Hours · 12 Months</p>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="bg-white border-b border-navy-200">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
          <Stat value={stats.verified_agencies ?? "—"} label="PSARA Verified Agencies" />
          <Stat value={stats.cities_covered ?? "—"} label="Cities Covered" />
          <Stat value={stats.security_professionals ?? "—"} label="Security Professionals" />
          <Stat value={stats.contracts_facilitated ?? "—"} label="Contracts Facilitated" />
          <Stat value={stats.customer_reviews ?? "—"} label="Verified Reviews" />
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <span className="brand-label text-brand">Security Services</span>
        <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">Everything you need, verified & managed</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cats.map((c) => {
            const Icon = CATEGORY_ICONS[c.group] || ShieldCheck;
            return (
              <Link to={`/services`} key={c.group} className="card-lift bg-white border border-navy-200 rounded-xl p-6 block" data-testid={`cat-${c.group.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="w-11 h-11 rounded-lg bg-navy-900 text-white flex items-center justify-center"><Icon size={20} /></div>
                <h3 className="mt-4 font-heading font-semibold text-lg text-navy-900">{c.group}</h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.services.slice(0, 4).map((s) => <span key={s} className="text-xs px-2 py-0.5 rounded bg-navy-100 text-navy-600">{s}</span>)}
                  {c.services.length > 4 && <span className="text-xs px-2 py-0.5 text-brand font-medium">+{c.services.length - 4} more</span>}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <span className="brand-label text-brand">How It Works</span>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Find. Compare. Hire. Manage.</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-6">
            {[[FileText, "Post Requirement", "Tell us your location, guards, shifts and budget."],
              [GitCompare, "Compare Quotes", "Get competitive quotations from verified agencies."],
              [Handshake, "Hire & Contract", "Accept the best quote and sign a digital contract."],
              [TrendingUp, "Manage Everything", "Attendance, replacements, invoices & performance."]].map(([Icon, t, d], i) => (
              <div key={t} className="relative">
                <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center"><Icon size={22} /></div>
                <span className="absolute top-0 right-0 font-heading text-5xl font-black text-white/5">{i + 1}</span>
                <h3 className="mt-4 font-heading font-semibold text-lg">{t}</h3>
                <p className="mt-2 text-navy-400 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED AGENCIES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="brand-label text-brand">Top Rated</span>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">Featured verified agencies</h2>
          </div>
          <Link to="/agencies"><Button variant="outline" className="border-navy-300">View all agencies <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {featured.map((a) => (
            <Link to={`/agencies/${a.id}`} key={a.id} className="card-lift bg-white border border-navy-200 rounded-xl overflow-hidden block" data-testid={`featured-${a.id}`}>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-lg bg-navy-900 text-white flex items-center justify-center font-heading font-bold">{a.name[0]}</div>
                  <VerifiedBadge verified={a.verified} />
                </div>
                <h3 className="mt-4 font-heading font-semibold text-lg text-navy-900">{a.name}</h3>
                <div className="flex items-center gap-2 mt-1 text-sm text-navy-500"><MapPin size={14} /> {a.head_office?.city}</div>
                <div className="mt-3 flex items-center gap-2"><Stars value={a.rating} /> <span className="text-sm text-navy-600">{a.rating}</span></div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center border-t border-navy-100 pt-4">
                  <Mini label="Exp" val={`${a.years_experience}y`} />
                  <Mini label="Guards" val={a.total_guards} />
                  <Mini label="From" val={inr(a.price_per_guard_month)} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TOTAL SOLUTION FOR AGENCIES */}
      <section className="bg-navy-50 border-y border-navy-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <span className="brand-label text-brand">For Security Agencies</span>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">A total solution for security agencies</h2>
          <p className="mt-3 text-navy-600 max-w-2xl">From incorporation to execution — GuardDekho helps you start, certify, staff and scale your security business.</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[[Building2, "Incorporation to Execution", "End-to-end support to set up, register and run your security agency."],
              [Wallet, "Finance Solutions for All Employees", "Payroll, salary advances and financial products for your entire workforce."],
              [BadgeCheck, "PSARA Certification", "Get PSARA licensed with expert compliance and documentation assistance."],
              [GraduationCap, "Guard Training Certification", "Certified guard training courses to upskill and qualify your staff."]].map(([Icon, t, d]) => (
              <div key={t} className="card-lift bg-white border border-navy-200 rounded-xl p-6" data-testid={`agency-solution-${t.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="w-11 h-11 rounded-lg bg-brand text-white flex items-center justify-center"><Icon size={20} /></div>
                <h3 className="mt-4 font-heading font-semibold text-navy-900">{t}</h3>
                <p className="mt-2 text-sm text-navy-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <Link to="/for-agencies"><Button className="mt-8 bg-navy-900 hover:bg-navy-800 h-11 px-7" data-testid="agency-solutions-cta">Explore Agency Solutions <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="rounded-2xl bg-brand p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl font-bold text-white tracking-tight">Ready to secure your premises?</h2>
            <p className="mt-2 text-white/90">Post your requirement and receive verified quotations in minutes.</p>
          </div>
          <Button size="lg" className="bg-white text-brand hover:bg-navy-900 hover:text-white h-12 px-8 font-semibold" onClick={() => navigate("/post-requirement")} data-testid="cta-post-btn">
            Request Quotations
          </Button>
        </div>
      </section>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-semibold text-navy-600 mb-1.5"><Icon size={13} /> {label}</label>
      {children}
    </div>
  );
}
function Mini({ label, val }) {
  return (<div><div className="font-heading font-bold text-navy-900 text-sm">{val}</div><div className="text-[10px] uppercase tracking-wide text-navy-400">{label}</div></div>);
}
